import type { ActionTree } from "vuex";
import type { Place } from "../utils/schema";
import type { State } from "./state";
import { setCookie } from "../utils/cookie";
import { Api } from "./index";
import * as Text from "../i18n/en-gb.json";
import moment from "moment/src/moment";


export default {
    setCalendarHasDoublePaneState: ({ commit }, doublePane: boolean) => {
        commit('setCalendarHasDoublePaneState', doublePane);
    },

    hideErrorMessage: ({ commit }) => {
        commit('setErrorMessageVisible', false);
    },

    showErrorMessage: ({ commit }) => {
        commit('setErrorMessageVisible', true);
    },

    updateAllGuestTotals: ({ commit }, info) => {
        commit('updateGuestsTotals', info);
    },

    updateAllGuestInputValues: ({ commit }, totals: string) => {
        commit('updateGuestInput', totals);
        commit('setCalendarText', Text.calendar.text);
        commit('setKeyCheckInOutText', Text.legend.checkInOut);
        commit('resetCalendarCheckInOutText');
        commit('clearSelectedDates');
    },

    activateClearState: ({ commit }) => {
        commit('updateClearState', true);
    },

    deActivateClearState: ({ commit }) => {
        commit('updateClearState', false);
    },

    setDefaultState: ({ commit }, defaultState: boolean) => {
        commit('updateDefaultState', defaultState);
    },

    forceReloadCalendar: ({ commit }, forceReload: boolean) => {
        commit('updateForceReload', forceReload);
    },

    clearAPiData: ({ state }) => {
        state.apiData = {};
        state.apiDates.place = {
            availableCheckInDates: [],
            availableCheckOutDates: [],
            disabledDates: [],
            allBookablesAPI: []
        }
    },

    /**
     * Perform initial calendar setup, including API call if required
     *
     * @param callback Optional callback to run after API request
     */
    calendarSetup: ({ dispatch, state }, callback?: Function) => {
        if ( state.clearState ) {
            // console.log("API DATA CLEAR")
            state.apiData = {};
            state.apiDates.place = {
                availableCheckInDates: [],
                availableCheckOutDates: [],
                disabledDates: [],
                allBookablesAPI: []
            }
        }

        if ( state.place.availability_cal ) {
            dispatch('setApiQuery', callback);
        } else {
            let dates = {
                start: moment().add(1, 'days').format('YYYY-MM-DD'),
                end: moment().add(18, 'months').format('YYYY-MM-DD')
            }
            dispatch('calendarRequestDates', dates);

            if (callback) {
                callback();
            }
        }
    },

    closedCalendar: ({ dispatch, state }) => {
        dispatch('hideWindow', 'calendar');
        dispatch('applyDates', state.selectedDate);

        if ( state.place.availability_cal ) {
            dispatch('queryBookablesWithSelectedDates', {
                dates: state.selectedDate,
                bookables: state.place.bookables
            });
        }
    },

    /**
     * Get bookable availability between two dates
     *
     * @param callback Optional callback to run after API request
     */
    setApiQuery: ({ dispatch, state }, callback?: Function) => {
        let dates = {}

        if (state.place.env == "development") {
            // change the start date below if you need to test different dates
            dates = {
                start: "2023-01-01",
                end: moment().add(3, 'months').format('YYYY-MM-DD')
            };
        } else {
            dates = {
                start: moment().add(1, 'days').format('YYYY-MM-DD'),
                end: moment().add(18, 'months').format('YYYY-MM-DD')
            };
        }

        dispatch('getApiResponse', {dates, callback});
    },

    setGuestInputsState: ({ commit, state }) => {
        const newRules = {
            adults: {
                allowed: true,
                max: 100,
                ipm_value: state.guestSelector.selector.adults.ipm_value
            },
            children: {
                allowed: (state.place.suitability.children) ? true : false,
                max: (state.place.suitability.children) ? 100 : 0,
                ipm_value: ( state.place.suitability.children ) ? state.guestSelector.selector.children.ipm_value : 0
            },
            babies: {
                allowed: (state.place.suitability.babies) ? true : false,
                max: (state.place.suitability.babies) ? 5 : 0,
                ipm_value: ( state.place.suitability.babies ) ? state.guestSelector.selector.babies.ipm_value : 0
            },
            pets: {
                allowed: (state.place.suitability.pets) ? true : false,
                max: (state.place.suitability.pets) ? 5 : 0,
                ipm_value: (state.place.suitability.pets) ? state.guestSelector.selector.pets.ipm_value : 0
            }
        }

        commit('updateAllowRules', newRules);
    },

    updateGuestsDatesSummary: ({ commit, state }) => {
        let totalguests = parseInt(String(state.guestSelector.selector.adults.ipm_value + state.guestSelector.selector.children.ipm_value));
        var totalguests_text = totalguests.toString() + ' Guest';

        // Stop annoying ESLint warning
        const $ = window.$;

        if (totalguests != 1) {
            totalguests_text = totalguests_text + 's';
        }

        // Need both check-in and check-out before we can show the summary
        if (state.selectedDate.length < 2) {
            return;
        }

        $('.guestsdates_guests').text(totalguests_text);

        const checkin_date = moment(state.selectedDate[0]).format("DD MMM YY");
        const checkout_date = moment(state.selectedDate[state.selectedDate.length - 1]).format("DD MMM YY");

        $('.guestsdates_dates').text(checkin_date + ' - ' + checkout_date);

        $('.guestsdates').removeClass('hidden');
        $('.accordion-ac').addClass('hidden');

        commit('toggleCalendarWindow');
    },

    checkGuestsAgainstTotalCapacity: ({ commit, state }, total: number) => {
        commit('lockGuestInput', total == state.place.totalCapacity);
    },

    toggleWindow: ({ commit }, toggle: "guest"|"calendar") => {
        if (toggle == 'guest') {
            commit('toggleGuestWindow');
        } else if (toggle == 'calendar') {
            commit('toggleCalendarWindow');
        }
    },

    hideWindow: ({ commit }, toggle: "guest"|"calendar") => {
        if (toggle == 'guest') {
            commit('hideGuestWindow');
        } else if (toggle == 'calendar') {
            commit('hideCalendarWindow');
        }
    },

    hideKeys: ({ commit, state }) => {
        if (state.place.availability_cal) {
            commit('hideKeyOnRequest');
        } else {
            commit('hideKeyCheckInOut');
        }
    },

    // TODO: Tackle this later - needs breaking up into smaller chunks
    dateSelectionController: (context, dateInfo) => {
        // Exit early if the date selected is hidden out of current month's calendar pane
        if ( !dateInfo.inMonth ) {
            return;
        }

        const numOfSelectedDates = context.state.selectedDate.length; // determine how many dates have been selected

        if ( numOfSelectedDates >= 2 ) {
            context.commit('clearSelectedDates');
        }

        if ( context.state.place.availability_cal ) {

            let dateObj = context.state.apiData[moment(dateInfo.date).format('YYYY-MM-DD')]; // get the rules for this date

            /*
             * TODO: Sometimes when opening a new tab the cookies from the
             * previous window will be set, but the URL might now reference
             * different dates. This can cause `dateInfo` to be passed in
             * with dates not known to the `apiData` store, causing an
             * error.
             *
             * Need to update cookie handling to make sure both the
             * `availability_enquiry` and `search_cookie__availability`
             * cookies have the same value / are updated consistently.
             */
            if ( typeof dateObj === "undefined" ) {
                context.commit('clearSelectedDates');
                return;
            }

            let step_nights = dateObj.stepNights;

            if (numOfSelectedDates < 1 || numOfSelectedDates >= 2) { // 1st Date
                // 1. it's a valid check in day as the minNights meets the requirements

                if (dateObj.minNights != 0) {
                    // 2. commit the date to the selected date array
                    context.commit('updateSelectedDates', moment(dateInfo.date).format('YYYY-MM-DD'));

                    // 3. update the min max calendar dates to the booking rules for this date
                    let startDateMinMaxDates = {
                        min: moment(dateInfo.date).add(dateObj.minNights, 'days').format('YYYY-MM-DD'),
                        max: moment(dateInfo.date).add(dateObj.maxNights, 'days').format('YYYY-MM-DD')
                    }

                    let dates = {
                        start: new Date(dateInfo.date),
                        end: new Date(startDateMinMaxDates.max)
                    }

                    context.commit('setMinMaxDates', dates)
                    context.commit('updateStartDateRules', startDateMinMaxDates)

                    // 4. block out/disable any dates that do not fit within the minNights
                    // Build array of dates between the min/max and check if they are valid checkouts
                    let availableDatesRange = [];
                    // check the min nights for the check in date
                    let minCheckOutDate = moment(dates.start).add(dateObj.minNights, 'days')
                    let maxCheckOutDate = moment(dates.end).format('YYYY-MM-DD')
                    let disabledDates = []

                    // ------------------------------------------------------
                    // Work out available dates, compensating for step nights
                    // ------------------------------------------------------
                    while (minCheckOutDate.isSameOrBefore(maxCheckOutDate, 'day')) {

                        var start = moment(dateInfo.date, "YYYY-MM-DD");
                        var end = moment(minCheckOutDate, "YYYY-MM-DD");

                        if( step_nights == 1 ){
                            availableDatesRange.push( { start: new Date(minCheckOutDate), end: new Date(minCheckOutDate) } );
                        }else{
                            // Need to floor the duration to compensate for GMT shifts making the duration slighly more than a single day
                            if( Math.floor( moment.duration(end.diff(start)).asDays()) % step_nights === 0 ){
                                availableDatesRange.push( { start: new Date(minCheckOutDate), end: new Date(minCheckOutDate) } );
                            }else{
                                disabledDates.push( {start: new Date(minCheckOutDate), end: new Date(minCheckOutDate) });
                            }
                        }

                        minCheckOutDate.add(1, 'days');

                    }


                    //console.log('availableDatesRange', availableDatesRange)

                    let minDisabledDate = moment(dates.start).add(1, 'days').format('YYYY-MM-DD');
                    let maxDisabledDate = moment(dates.start).add(dateObj.minNights, 'days').format('YYYY-MM-DD');

                    var pickerAttributes = [
                        {
                            key: 'checkIn',
                            contentStyle: context.state.pickerStyles.disabledDates,
                            dates: context.state.apiDates.place.availableCheckInDates
                        },
                        {
                            key: 'selectedDate',
                            contentStyle: context.state.pickerStyles.selectedDates,
                            dates: dateInfo.date,
                            order: 1
                        },
                        {
                            key: 'availableDays',
                            bar: context.state.pickerStyles.checkInOutBar,
                            contentStyle: context.state.pickerStyles.availableDates,
                            dates: availableDatesRange,
                            order: 5
                        },
                        {
                            key: 'disabledDatesDueToMinNightsStay',
                            contentStyle: context.state.pickerStyles.availableNonSelectableDates,
                            dates: { start: new Date(minDisabledDate), end: new Date(maxDisabledDate) },
                            order: 4
                        },
                        context.state.pickerStyles.today
                    ]

                    if( disabledDates.length > 0 ){
                        disabledDates.forEach(function(disabledDate) {

                            pickerAttributes.push({
                                key: 'disabledDatesDueToMinNightsStay',
                                contentStyle: context.state.pickerStyles.availableNonSelectableDates,
                                dates: disabledDate,
                                order: 10
                            })

                        })

                    }

                    context.commit('setPickerAttributes', pickerAttributes);

                    // 5. Update calendar text
                    context.commit('updateCalendarTitle', 'Select your check-out');
                    context.commit('setCalendarText', moment(dateInfo.date).format('D MMM YY') + context.state.calendarTextSeperator.medium + ' Check out');
                    context.commit('setKeyCheckInOutText', 'Check out day');
                    context.commit('setCalendarCheckInText', moment(dateInfo.date).format('D MMM YY'));
                    context.commit('setCalendarCheckOutText', Text.calendar.checkOut);
                }
            }

            if (numOfSelectedDates === 1) { // Check-out date
                // We need to add a day to the 'startDateRules.max' so the 'between' works later on
                let selectableDates = {
                    start: moment(context.state.apiDates.startDateRules.min).subtract(1, 'days').format('YYYY-MM-DD'),
                    end: moment(context.state.apiDates.startDateRules.max).add(1, 'days').format('YYYY-MM-DD')
                }

                if (!moment(dateInfo.date).isBetween(selectableDates.start, selectableDates.end)) {
                    return;
                }

                // let isDateNotSelectable = true
                //
                //Get 'selectable' dates set on the first click
                // if( typeof context.state.pickerAttributes[3] !== 'undefined' && context.state.pickerAttributes[3].dates.length > 0){
                //
                //     console.log(context.state.pickerAttributes[3].dates)
                //
                //     context.state.pickerAttributes[3].dates.forEach(function(allowedDate) {
                //         if( moment(dateInfo.date).isSame(allowedDate.end) ){
                //             isDateNotSelectable = false
                //         }
                //     })
                //
                // }
                //
                // if( isDateNotSelectable ){
                //     console.log("date not selectable!")
                //     return;
                // }

                context.commit('updateSelectedDates', new Date(dateInfo.date));

                // Fill in the rest of the dates
                let startDate = moment(context.state.selectedDate[0]);
                let endDate = moment(context.state.selectedDate[1]);
                let selectedDatesArr = [];

                while (startDate.isSameOrBefore(endDate)) {
                    context.commit('updateSelectedDates', moment(startDate).format('YYYY-MM-DD'));
                    selectedDatesArr.push(new Date(startDate));
                    startDate.add(1, 'days');
                }

                context.commit('setCalendarText', moment(context.state.selectedDate[0]).format('D MMM YY')+ context.state.calendarTextSeperator.small +endDate.format('D MMM YY'));
                context.commit('setKeyCheckInOutText', Text.legend.checkInOut);
                context.commit('setCalendarCheckOutText', endDate.format('D MMM YY'));
                context.commit('updateCalendarTitle', 'Apply your dates');
                context.commit('datesApplied', true);

                // Update nights
                let nights = (endDate.diff(context.state.selectedDate[0], 'days'));
                let nightsText = (nights > 1) ? nights = '('+nights+' nights)' : nights = '('+nights+' night)';
                context.commit('setNightsText', nightsText);

                context.commit('removeStartAndEndDates');

                // Build new object of min & max dates
                let minMaxDates = {
                    start: moment().add(1, 'days').format('YYYY-MM-DD'),
                    end: context.state.maxEndDate
                };

                // commit the minmax dates to mutations
                context.commit('setMinMaxDates', minMaxDates);

                context.commit('setPickerAttributes', [
                    {
                        key: 'checkIn',
                        bar: context.state.pickerStyles.checkInOutBar,
                        contentStyle: context.state.pickerStyles.availableDates,
                        dates: context.state.apiDates.place.availableCheckInDates
                    },
                    {
                        key: 'checkOut',
                        contentStyle: context.state.pickerStyles.availableNonSelectableDates,
                        dates: context.state.apiDates.place.availableCheckOutDates
                    },
                    {
                        key: 'selectedDays',
                        contentStyle: context.state.pickerStyles.selectedDates,
                        dates: selectedDatesArr,
                        order: 1
                    },
                    context.state.pickerStyles.today
                ]);
            }
        } else {
            // On request
            if (numOfSelectedDates < 1 || numOfSelectedDates >= 2) { // 1st Date
                let dates = {
                    start: moment(dateInfo.date).format('YYYY-MM-DD'),
                    end: moment().add(18, 'months').format('YYYY-MM-DD')
                }

                context.commit('updateSelectedDates', moment(dateInfo.date).format('YYYY-MM-DD'));
                context.commit('setPickerAttributes', [
                    {
                        key: 'request',
                        contentStyle: context.state.pickerStyles.requestDates,
                        dates: dates
                    },
                    {
                        key: 'selectedDate',
                        contentStyle: context.state.pickerStyles.selectedDates,
                        dates: dateInfo.date,
                    },
                    context.state.pickerStyles.today

                ]);
                context.commit('updateCalendarTitle', 'Select your check-out');
                context.commit('setCalendarText', moment(dateInfo.date).format('D MMM YY') + context.state.calendarTextSeperator.medium + 'Check out');
                context.commit('setCalendarCheckInText', moment(dateInfo.date).format('D MMM YY'));
            }
            if (numOfSelectedDates === 1) { // Check-out date
                let startDate = moment(context.state.selectedDate[0]);

                if (moment(dateInfo.date).isSameOrBefore(startDate)) {
                    return;
                }
                context.commit('updateSelectedDates', new Date(dateInfo.date));

                let endDate = moment(context.state.selectedDate[1]);
                let selectedDatesArr = [];

                while (startDate.isSameOrBefore(endDate)) {
                    context.commit('updateSelectedDates', moment(startDate).format('YYYY-MM-DD'));
                    selectedDatesArr.push(new Date(startDate));
                    startDate.add(1, 'days');
                }

                context.commit('setCalendarText', moment(context.state.selectedDate[0]).format('D MMM YY') + context.state.calendarTextSeperator.small + endDate.format('D MMM YY'));
                context.commit('setCalendarCheckOutText', endDate.format('D MMM YY'));
                context.commit('updateCalendarTitle', 'Apply your dates');
                context.commit('datesApplied', true);

                // Update nights
                let nights = (endDate.diff(context.state.selectedDate[0], 'days'));
                let nightsText = (nights > 1) ? nights = '('+nights+' nights)' : nights = '('+nights+' night)';
                context.commit('setNightsText', nightsText);

                context.commit('removeStartAndEndDates');

                let dates = {
                    start: moment().add(1, 'days').format('YYYY-MM-DD'),
                    end: moment().add(18, 'months').format('YYYY-MM-DD')
                }

                context.commit('setMinMaxDates', dates);
                context.commit('setPickerAttributes', [
                    {
                        key: 'request',
                        contentStyle: context.state.pickerStyles.requestDates,
                        dates: dates
                    },
                    {
                        key: 'selectedDate',
                        contentStyle: context.state.pickerStyles.selectedDates,
                        dates: selectedDatesArr,
                        order: 1
                    },
                    context.state.pickerStyles.today

                ]);
            }
        }
    },

    applyDates: ({ commit, dispatch }, dates: any[]) => {
        if (dates.length > 1) {
            commit('datesApplied', true)
            dispatch({
                type: 'trackGaEvent',
                eventCategory: 'Availability calendar',
                eventAction: 'Date picker applied'
            })
        } else {
            commit('datesApplied', false)
        }
    },

    updateSelectedNights: ({ commit }, nights: number) => {
        commit('setNightsTotal', nights);
    },

    updateRequiredCapacity: ({ commit, state }) => {
        const totalDayCapacity = (state.guestSelector.selector.adults.ipm_value + state.guestSelector.selector.children.ipm_value);
        const totalRequiredCapacity = {
            daily: totalDayCapacity,
            total: (totalDayCapacity * state.requirements.totalNights)
        };

        commit('setRequiredCapacity', totalRequiredCapacity);
    },

    calendarRequestDates: ({ commit, state }, dates) => {
        commit('clearSelectedDates');
        commit('setCalendarText', Text.calendar.text);
        commit('resetCalendarCheckInOutText');
        commit('setPickerAttributes', [
            {
                key: 'request',
                contentStyle: state.pickerStyles.requestDates,
                dates: dates
            },
            state.pickerStyles.today
        ]);
    },

    getApiResponse: ({ commit, state }, dates) => {
        const callback = dates.callback ? dates.callback : null;

        // TODO: Update options passing at other call sites
        if (dates.dates) dates = dates.dates;

        // determine how many dates have been selected
        let numOfSelectedDates = state.selectedDate.length;

        if ( numOfSelectedDates != 1 ) {
            commit('clearSelectedDates');
        }

        commit('setCalendarText', Text.calendar.text);
        commit('setKeyCheckInOutText', Text.legend.checkInOut);
        commit('resetCalendarCheckInOutText');

        const place_id = state.place.id;
        const guests = state.requirements.totalGuests;

        // Set to a loading state
        commit('loading', true);

        /**
         * TODO:
         *
         * Functionality copied from old code, should this be a dynamic
         * range instead?
         */
        const start_date = dates ? dates.start : "2019-06-01";
        const end_date = dates ? dates.end : "2019-11-01";

        // Make a call to the place availability API
        Api.queryPlace(
            place_id,
            guests,
            start_date,
            end_date
        ).then((response) => {
            const apiResponse = response.calendar;

            type StartAndEndDates = {
                start: moment,
                end: moment
            };

            const dateArrays: {
                availableCheckInDates: StartAndEndDates[];
                availableCheckOutDates: StartAndEndDates[];
                disabledDates: StartAndEndDates[];
                allBookablesAPI: Place["bookables"][];
            } = {
                availableCheckInDates: [],
                availableCheckOutDates: [],
                disabledDates: [],
                allBookablesAPI: []
            };

            commit('updateClearState', false);
            commit('setAvailabilityData', apiResponse);

            if (callback) callback();

            Object.keys(apiResponse).forEach(function(item) {
                const current = apiResponse[item];
                let showAsAvailable = false;

                if (current.totalCapacity >= guests && current.minNights > 0) {
                    dateArrays.availableCheckInDates.push({ start: moment(item), end: moment(item) });
                    showAsAvailable = true;
                } else if (current.prev && apiResponse[current.prev] && apiResponse[current.prev].totalCapacity >= guests) {
                    dateArrays.availableCheckOutDates.push({ start: moment(item), end: moment(item) });
                    showAsAvailable = true;
                }

                if (!showAsAvailable) {
                    dateArrays.disabledDates.push({ start: moment(item), end: moment(item) });
                }

                dateArrays.allBookablesAPI.push(current.bookables);
            });

            let bookablesFromApi = dateArrays.allBookablesAPI.reduce<Place["bookables"][]>((acc, val) => acc.concat(val), []);
            let bookableNames = bookablesFromApi.filter(function(item, i, ar) {
                return ar.indexOf(item) === i;
            });

            commit('updateBookables', bookableNames);
            commit('setAPIDates', dateArrays);

            //TODO currently the date store is duplicating dates based on overlap
            // context.state.apiDates.place.availableCheckInDates.forEach(function(item) {
                // console.log(item.start.format('YYYY-MM-DD'))
            // })

            if (numOfSelectedDates != 1) {
                commit('setPickerAttributes', [
                    {
                        key: 'checkIn',
                        bar: state.pickerStyles.checkInOutBar,
                        contentStyle: state.pickerStyles.availableDates,
                        dates: state.apiDates.place.availableCheckInDates
                    },
                    {
                        key: 'checkOut',
                        contentStyle: state.pickerStyles.availableNonSelectableDates,
                        dates: state.apiDates.place.availableCheckOutDates
                    },
                    state.pickerStyles.today
                ]);

                // let todayPlus18Months = moment().add(18, 'months')
                let today = moment()
                // let startDate = moment()
                let maxEndDate = response.maxEndDate

                let minMaxDates = {}

                if ( state.place.env == 'development' ) {
                    minMaxDates = {
                        start: "2019-01-01",
                        end: maxEndDate
                    };
                } else {
                    minMaxDates = {
                        start: today.add(1, 'days').format('YYYY-MM-DD'),
                        end: maxEndDate
                    };
                }

                commit('setMaxEndDate', maxEndDate);
                commit('setMinMaxDates', minMaxDates);
            }

            commit('loading', false);
        }).catch((error: Error) => {
            console.error(`Failed to query place: ${error.message}`);

            commit('loading', false);
        });
    },

    queryBookablesWithSelectedDates: ({ commit }, info: {bookables: [{id: string}], dates: (string|Date)[]}) => {
        let bookablesAvailability: Array<{bookable: any, dates: any}> = [];

        info.bookables.forEach((item) => {
            const start_date = moment(info.dates[0]).format('YYYY-MM-DD');
            const end_date = moment(info.dates[info.dates.length - 1]).format('YYYY-MM-DD');

            Api.queryBookable(
                item.id,
                start_date,
                end_date
            ).then((response) => {
                const availableBookables = response.calendar;

                /*
                 * Get all the dates each bookable is available AND has a
                 * total capacity greater than 0
                 */
                const availableDates = Object.values(availableBookables)
                    .filter((bookable) => bookable.totalCapacity > 0)
                    .map((bookable) => ({
                        start: new Date(bookable.date),
                        end: new Date(bookable.date)
                    }));

                bookablesAvailability.push({
                    'bookable' : item,
                    'dates' : availableDates
                });
            }).catch((error: Error) => {
                console.error(`Failed to query bookable: ${error.message}`);
            });
        });

        commit('updateBookablesAvailability', bookablesAvailability);
    },

    getBookableCalendarData: ({ commit, state }, queryParams: { bookableId: string, startDate: string, endDate: string }) => {
        const guests = state.requirements.totalGuests;
        const { bookableId, startDate, endDate } = queryParams;

        // Process for paginating Bookable calendars
        // - Query API for today + 2 months
        // - Get maxdate from API and fix the maximum scroll date to this date
        // - On pane interaction
        // - - requery API up to max date getting two new months each time
        // - - update state.bookableVisibleMonths to keep track of current visible months
        Api.queryBookable(bookableId, startDate, endDate).then((response) => {
            const bookableDates = response.calendar;

            type StartAndEndDates = {
                start: moment,
                end: moment
            };

            const calendar: {
                availableCheckInDates: StartAndEndDates[];
                availableCheckOutDates: StartAndEndDates[];
                disabledDates: StartAndEndDates[];
            } = {
                availableCheckInDates: [],
                availableCheckOutDates: [],
                disabledDates: []
            };

            commit('setMinMaxDates', {
                start: new Date(),
                end: response.maxEndDate
            });

            Object.keys(bookableDates).forEach((date) => {
                let bookable = bookableDates[date];

                if (bookable.totalCapacity == 0) {
                    // console.log('not-available')
                    calendar.disabledDates.push({ start: moment(date), end: moment(date) });
                } else if (bookable.minNights > 0 && bookable.totalCapacity >= guests) {
                    // console.log('available-for-checkin')
                    calendar.availableCheckInDates.push({ start: moment(date), end: moment(date) });
                } else if (bookable.minNights == 0 && bookable.totalCapacity >= guests) {
                    // console.log('available')
                    calendar.availableCheckOutDates.push({ start: moment(date), end: moment(date) });
                } else {
                    // console.log('not-available')
                    calendar.disabledDates.push({ start: moment(date), end: moment(date) });
                }
            });

            commit('updateBookablesCalendarDates', calendar);
            commit('setBookablesAttributes', [
                {
                    key: 'checkIn',
                    bar: state.pickerStyles.checkInOutBar,
                    contentStyle: state.pickerStyles.availableDatesBookable,
                    dates: state.bookableCalendar.availableCheckInDates,
                },
                {
                    key: 'checkOut',
                    contentStyle: state.pickerStyles.availableNonSelectableDates,
                    dates: state.bookableCalendar.availableCheckOutDates
                },
            ]);
        }).catch((error: Error) => {
            console.error(`Failed to query bookable: ${error.message}`);
        });
    },

    clearBookablesCalendarDates: ({ state }) => {
        state.bookableCalendar.availableCheckInDates = [];
        state.bookableCalendar.availableCheckOutDates = [];
        state.bookableCalendar.disabledDates = [];
    },

    showBookableCalendar: ({ commit }, queryParams: { bookableId: number }) => {
        commit('setBookingCalendarToShow', queryParams.bookableId);
    },

    /**
     * Set the enquiry cookie and redirect to the enquiry form
     */
    enquiryCookieController: ({ state }) => {
        const checkIn = state.selectedDate[0];
        const checkOut = state.selectedDate[state.selectedDate.length - 1];

        const cookieData = {
            'guests': {
                'Adults': state.guestSelector.selector.adults.ipm_value,
                'Children': state.guestSelector.selector.children.ipm_value,
                'Babies': state.guestSelector.selector.babies.ipm_value,
                'Pets': state.guestSelector.selector.pets.ipm_value
            },
            'checkin': moment(checkIn).format("DD-MM-YYYY"),
            'checkout': moment(checkOut).format("DD-MM-YYYY"),
        }

        // TODO: This expiry date logic has never worked?
        // On the live site, the cookie expiry has always been 'session'...
        setCookie("availability_enquiry", cookieData, moment().add(30, 'days').utc().format());

        // Redirect!
        let redirectUrl = state.place.url + 'enquiry/';
        
        // Only add date parameters if dates have been selected
        if (state.selectedDate.length >= 2) {
            const checkinParam = moment(checkIn).format("DD-MM-YYYY");
            const checkoutParam = moment(checkOut).format("DD-MM-YYYY");
            redirectUrl += '?arrival_date=' + encodeURIComponent(checkinParam) + '&departure_date=' + encodeURIComponent(checkoutParam);
        }
        
        window.location.href = redirectUrl;
    },

    /**
  	 * GA tracking. Use like this:
  	 *
  	 *   this.$store.dispatch({
     *       type: 'trackGaEvent',
     *       eventCategory: 'foo',
     *       eventAction: 'bar',
     *       eventLabel: 'zar'
     *   })
  	 */
    trackGaEvent: function({ getters }, trackingData) {
        // Exit early if GA not available
        if ( typeof window.ga !== "function" ) {
            return;
        }

        window.dataLayer.push({
            "eventCategory": trackingData.eventCategory,
            "eventAction": trackingData.eventAction,
            "eventLabel": getters.eventLabel,
            "event": "interactionEvent"
        });
    }
} as ActionTree<State, State>;
